export default {
  terms: {
    title: 'Términos de Servicio',
    lastUpdated: 'Última actualización:',
    lastUpdatedDate: 'August 13, 2026',
    seoDescription:
      'Read the Geofleet.ai Terms of Service governing use of our website, apps, APIs, integrations, and related logistics services.',
    backToHome: '← Volver al inicio',
    intro: [
        'These Terms of Service ("Terms") govern your access to and use of Geofleet.ai, including our website, web applications, mobile applications, APIs, integrations, and related services (collectively, the "Services"). The Services are provided by Geofleet.ai ("Geofleet", "we", "us", or "our").',
        'By accessing or using the Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you are using the Services on behalf of a company or other organization, you represent and warrant that you have the authority to bind that organization to these Terms.',
        'If you do not agree with these Terms, you must not access or use the Services.',
    ],
    sectionOrder: [
      'agreement',
      'description',
      'eligibility',
      'accounts',
      'acceptableUse',
      'customerData',
      'googleIntegration',
      'thirdParty',
      'aiFeatures',
      'subscription',
      'intellectualProperty',
      'feedback',
      'confidentiality',
      'dataProtection',
      'security',
      'serviceAvailability',
      'disclaimers',
      'limitation',
      'indemnification',
      'suspension',
      'changesToServices',
      'changesToTerms',
      'governingLaw',
      'severability',
      'contact',
    ],
    sections: {
      agreement: {
        title: '1. Agreement to Terms',
        paragraphs: [
          'By accessing or using the Services, you agree to these Terms and any additional terms or policies referenced herein, including our <0>Privacy Policy</0>.',
          'These Terms apply to all users of the Services, including visitors, trial users, customers, administrators, employees, and other authorized users.',
          'If you enter into a separate written agreement, subscription agreement, order form, statement of work, or other commercial agreement with Geofleet, that agreement will govern to the extent it conflicts with these Terms.',
        ],
      },
      description: {
        title: '2. Description of Services',
        paragraphs: [
          'Geofleet provides a logistics and delivery management platform designed to help businesses manage and automate transportation, fleet, delivery, dispatch, routing, tracking, and related operational workflows.',
        ],
        listIntro: 'Depending on the plan and configuration, the Services may include:',
        items: [
          'Fleet and vehicle management',
          'Driver management',
          'Order and delivery management',
          'Dispatch and allocation',
          'Route planning and optimization',
          'Geofencing and location tracking',
          'Proof of delivery and electronic documentation',
          'Delivery and operational reporting',
          'Notifications and alerts',
          'Analytics and business intelligence',
          'Electric vehicle and fleet management features',
          'APIs and third-party integrations',
          'Mobile applications',
          'AI-assisted and automation features',
        ],
        paragraphsAfter: [
          'Specific features available to you may depend on your subscription plan, configuration, location, or commercial agreement.',
          'We may add, modify, improve, suspend, or discontinue features from time to time. Where reasonably practicable, we will provide notice of material changes that substantially affect the Services.',
        ],
      },
      eligibility: {
        title: '3. Eligibility and Authority',
        paragraphs: [
          'You must be at least 18 years old to use the Services.',
        ],
        listIntro: 'If you access or use the Services on behalf of an organization, you represent that:',
        items: [
          'You have authority to bind the organization to these Terms.',
          'The organization has the legal right to use the data and information submitted to the Services.',
          'Your use of the Services complies with applicable laws and regulations.',
        ],
      },
      accounts: {
        title: '4. Accounts and Account Security',
        paragraphs: [
          'Certain Services require you to create an account.',
        ],
        listIntro: 'You agree to:',
        items: [
          'Provide accurate and complete information.',
          'Keep your account information current.',
          'Maintain the confidentiality of your login credentials.',
          'Restrict access to your account to authorized users.',
          'Notify us promptly if you believe your account has been compromised.',
          'Be responsible for activities performed through your account by authorized users.',
        ],
        paragraphsAfter: [
          'You are responsible for maintaining appropriate security controls for your organization\'s users, credentials, devices, and access permissions.',
          'We are not responsible for unauthorized access resulting from your failure to maintain appropriate account security.',
        ],
      },
      acceptableUse: {
        title: '5. Acceptable Use',
        listIntro: 'You agree not to:',
        items: [
          'Use the Services in violation of applicable law or regulation.',
          'Access or use another user\'s account without authorization.',
          'Attempt to gain unauthorized access to the Services or related systems.',
          'Reverse engineer, decompile, disassemble, or attempt to derive source code from the Services except where expressly permitted by law.',
          'Circumvent security, authentication, access-control, or usage restrictions.',
          'Interfere with or disrupt the Services or their underlying infrastructure.',
          'Introduce malware, viruses, malicious code, or other harmful material.',
          'Use the Services to distribute spam or unlawful content.',
          'Scrape or systematically extract information from the Services except through authorized functionality or APIs.',
          'Use the Services to infringe intellectual property, privacy, or other rights of third parties.',
          'Use the Services to process data that you do not have the legal right or authorization to process.',
          'Use the Services to develop or provide a competing service by unauthorized means.',
          'Attempt to circumvent usage limits, subscription restrictions, or other technical controls.',
        ],
        paragraphsAfter: [
          'We may suspend or restrict access where reasonably necessary to protect the Services, our users, or third parties, or where we reasonably believe these Terms have been violated.',
        ],
      },
      customerData: {
        title: '6. Customer Data',
        paragraphs: [
          'For purposes of these Terms, "Customer Data" means information, content, records, files, operational information, personal information, or other data submitted to or processed through the Services by or on behalf of a customer.',
          'As between you and Geofleet, you retain ownership of your Customer Data.',
        ],
        listIntro: 'You grant Geofleet a limited, non-exclusive right to access, host, process, transmit, store, and otherwise use Customer Data only as reasonably necessary to:',
        items: [
          'Provide and operate the Services.',
          'Perform requested functionality.',
          'Provide customer support.',
          'Maintain security and reliability.',
          'Prevent fraud and abuse.',
          'Comply with applicable legal obligations.',
          'Enforce these Terms and applicable agreements.',
        ],
        paragraphsAfter: [
          'You are responsible for ensuring that you have all necessary rights, permissions, notices, and legal bases required to provide Customer Data to Geofleet and to permit Geofleet to process that data.',
        ],
      },
      googleIntegration: {
        title: '7. Google Workspace and Google Sheets Integration',
        paragraphs: [
          'Geofleet may provide integrations with third-party services, including Google Workspace, Google Drive, and Google Sheets.',
          'The Google Sheets integration allows authorized users to discover available Google Sheets, select a spreadsheet and sheet/tab, and import information into Geofleet.',
          'When you connect a Google account, Geofleet requests only the permissions necessary to provide the selected functionality.',
        ],
        listIntro: 'The Google integration may use:',
        labeledItems: [
          {
            label: 'Google Drive metadata read-only access',
            text: 'to discover and list available files, including Google Sheets, and identify resources such as file name, file ID, and file type.',
          },
          {
            label: 'Google Sheets read-only access',
            text: 'to read the contents of the spreadsheet and sheet/tab selected by the user.',
          },
        ],
        paragraphsAfter: [
          'Geofleet does not use these permissions to create, modify, or delete Google Drive files or Google Sheets.',
          'By connecting a Google account, you authorize Geofleet to access the information necessary to provide the functionality you have requested, subject to the permissions displayed during Google\'s authorization process.',
          'You may revoke the authorization granted to Geofleet through your Google Account settings or through applicable functionality provided by Geofleet.',
          'You represent that you have the necessary rights and authorization to access and import information from the Google account, Google Drive files, and Google Sheets that you connect to Geofleet.',
          'The handling of information obtained through Google Workspace integrations is described in our <0>Privacy Policy</0>.',
        ],
      },
      thirdParty: {
        title: '8. Third-Party Services and Integrations',
        paragraphs: [
          'The Services may integrate with or rely on third-party services, APIs, platforms, hosting providers, payment processors, mapping providers, communication services, analytics providers, or other technology providers.',
          'Your use of third-party services may be subject to their respective terms and privacy policies.',
          'Geofleet is not responsible for third-party services outside our control, including their availability, functionality, security, or policies.',
          'Where you enable a third-party integration, you authorize Geofleet to exchange the information necessary to provide the functionality you have requested, subject to applicable permissions and policies.',
        ],
      },
      aiFeatures: {
        title: '9. AI and Automation Features',
        paragraphs: [
          'Geofleet may provide artificial intelligence, machine learning, automation, analytics, recommendation, or decision-support features.',
          'These features are intended to assist users and do not necessarily replace human judgment.',
          'You remain responsible for reviewing and validating outputs before relying on them for operational, commercial, legal, safety, or other material decisions.',
          'Unless expressly stated otherwise, AI-generated or automated outputs may contain errors or omissions and are provided for informational and operational assistance.',
          'Information obtained through Google Workspace APIs is not used by Geofleet to create, train, or improve generalized or foundational artificial intelligence or machine learning models.',
        ],
      },
      subscription: {
        title: '10. Subscription, Fees, and Payment',
        paragraphs: [
          'Where applicable, you agree to pay the fees associated with your selected subscription, usage, or services in accordance with the applicable pricing, order form, subscription agreement, or other commercial terms.',
          'Fees may be billed monthly, annually, based on usage, or according to another agreed billing schedule.',
        ],
        listIntro: 'Unless otherwise stated in writing:',
        items: [
          'Fees are non-refundable once charged.',
          'Taxes and applicable governmental charges may be additional.',
          'You are responsible for providing accurate billing information.',
          'Failure to pay applicable fees may result in suspension or termination of access.',
        ],
        paragraphsAfter: [
          'We may change pricing with reasonable advance notice. Changes will generally apply to future billing periods unless otherwise agreed.',
        ],
      },
      intellectualProperty: {
        title: '11. Intellectual Property',
        listIntro: 'Geofleet and its licensors retain all rights, title, and interest in the Services, including:',
        items: [
          'Software',
          'Source and object code',
          'User interfaces',
          'Designs',
          'Documentation',
          'Branding',
          'Logos',
          'Trademarks',
          'Proprietary technology',
          'Non-customer-generated content',
        ],
        paragraphsAfter: [
          'Except as expressly permitted by these Terms, no rights are granted to you.',
          'Subject to your compliance with these Terms, Geofleet grants you a limited, non-exclusive, non-transferable, non-sublicensable right to access and use the Services for your internal business purposes during your subscription or authorized access period.',
          'You retain ownership of your Customer Data.',
        ],
      },
      feedback: {
        title: '12. Feedback',
        paragraphs: [
          'If you voluntarily provide suggestions, ideas, feedback, or recommendations regarding the Services, you grant Geofleet the right to use such feedback without restriction or obligation to compensate you, provided that such use does not disclose your confidential information.',
        ],
      },
      confidentiality: {
        title: '13. Confidentiality',
        paragraphs: [
          'Each party may receive confidential information from the other party in connection with the Services.',
          'The receiving party agrees to use reasonable measures to protect confidential information and to use it only for purposes related to the applicable business relationship.',
        ],
        listIntro: 'Confidential information does not include information that:',
        items: [
          'Is publicly available without breach of an obligation.',
          'Was already lawfully known to the receiving party.',
          'Is independently developed without use of confidential information.',
          'Is lawfully received from a third party without confidentiality restrictions.',
          'Must be disclosed by law or legal process.',
        ],
      },
      dataProtection: {
        title: '14. Data Protection and Privacy',
        paragraphs: [
          'Our collection, use, processing, storage, and disclosure of personal information are described in our <0>Privacy Policy</0>.',
          'You are responsible for ensuring that your use of the Services and the Customer Data you submit complies with applicable privacy and data protection laws.',
          'Where required, you are responsible for providing appropriate notices and obtaining appropriate consents or other legal permissions from individuals whose information you submit to the Services.',
        ],
      },
      security: {
        title: '15. Security',
        paragraphs: [
          'We maintain reasonable technical and organizational safeguards designed to protect the Services and information processed through them.',
          'However, no system or method of transmission or storage can be guaranteed to be completely secure.',
          'You are responsible for implementing appropriate security measures on your side, including account security, access controls, device security, and appropriate user permissions.',
        ],
      },
      serviceAvailability: {
        title: '16. Service Availability',
        paragraphs: [
          'We will use commercially reasonable efforts to maintain the availability of the Services.',
        ],
        listIntro: 'However, the Services may occasionally be unavailable due to:',
        items: [
          'Scheduled maintenance',
          'Emergency maintenance',
          'Infrastructure failures',
          'Internet or telecommunications failures',
          'Third-party service failures',
          'Security incidents',
          'Events outside our reasonable control',
        ],
        paragraphsAfter: [
          'We do not guarantee uninterrupted or error-free availability unless expressly agreed under a separate service-level agreement.',
        ],
      },
      disclaimers: {
        title: '17. Disclaimers',
        paragraphs: [
          'To the maximum extent permitted by applicable law, the Services are provided on an "as is" and "as available" basis.',
          'Geofleet disclaims warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, title, non-infringement, accuracy, availability, and suitability for a particular purpose.',
        ],
        listIntro: 'We do not warrant that:',
        items: [
          'The Services will always be available.',
          'The Services will be error-free.',
          'Information or automated outputs will always be accurate.',
          'Route, delivery, location, ETA, or optimization results will always be accurate.',
          'The Services will meet every operational requirement.',
          'Third-party integrations will remain available or unchanged.',
        ],
      },
      limitation: {
        title: '18. Limitation of Liability',
        paragraphs: [
          'To the maximum extent permitted by law, Geofleet and its affiliates, officers, directors, employees, contractors, and agents will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, including loss of profits, revenue, business opportunities, goodwill, or data.',
          'To the maximum extent permitted by applicable law, Geofleet\'s aggregate liability arising out of or relating to the Services or these Terms will not exceed the fees actually paid by you to Geofleet for the Services during the twelve (12) months preceding the event giving rise to the claim.',
          'Nothing in these Terms excludes liability that cannot legally be excluded or limited under applicable law.',
        ],
      },
      indemnification: {
        title: '19. Indemnification',
        listIntro: 'You agree to defend, indemnify, and hold harmless Geofleet and its affiliates, officers, directors, employees, contractors, and agents from claims, damages, liabilities, losses, costs, and expenses, including reasonable legal fees, arising from:',
        items: [
          'Your use of the Services.',
          'Your violation of these Terms.',
          'Your violation of applicable law.',
          'Your violation of third-party rights.',
          'Customer Data submitted by you where you did not have the necessary rights or authorization.',
        ],
      },
      suspension: {
        title: '20. Suspension and Termination',
        paragraphs: [
          'You may stop using the Services at any time.',
        ],
        listIntro: 'We may suspend or terminate access if:',
        items: [
          'You materially breach these Terms.',
          'You fail to pay applicable fees.',
          'Your use presents a security or legal risk.',
          'Suspension is reasonably necessary to protect the Services or other users.',
          'Required by law or a third-party platform provider.',
        ],
        paragraphsAfter: [
          'Upon termination, your right to access the Services will cease, subject to any applicable transition, export, retention, or deletion obligations under your subscription or written agreement.',
          'Provisions that by their nature should survive termination will survive, including intellectual property, confidentiality, disclaimers, limitation of liability, indemnification, and governing law.',
        ],
      },
      changesToServices: {
        title: '21. Changes to the Services',
        paragraphs: [
          'We may modify or discontinue features of the Services from time to time.',
          'Where reasonably practicable, we will provide notice of material changes.',
          'We are not liable for modification, suspension, or discontinuation of a feature or third-party integration, except where otherwise agreed in writing.',
        ],
      },
      changesToTerms: {
        title: '22. Changes to These Terms',
        paragraphs: [
          'We may update these Terms from time to time.',
          'The updated Terms will be published on this page with a revised "Last Updated" date.',
          'Material changes may also be communicated by email, through the Services, or by another reasonable method.',
          'Your continued use of the Services after the effective date of revised Terms constitutes acceptance of the updated Terms. If you do not agree to the revised Terms, you must stop using the Services.',
        ],
      },
      governingLaw: {
        title: '23. Governing Law and Disputes',
        paragraphs: [
          'These Terms are governed by and construed in accordance with the laws of India, without regard to conflict-of-law principles.',
          'Any dispute arising out of or relating to these Terms or the Services shall be subject to the jurisdiction of the competent courts in India, subject to any mandatory rights or remedies available under applicable law.',
        ],
      },
      severability: {
        title: '24. Severability',
        paragraphs: [
          'If any provision of these Terms is found to be invalid, unlawful, or unenforceable, that provision will be interpreted to the maximum extent permitted by law, and the remaining provisions will remain in full force and effect.',
        ],
      },
      contact: {
        title: '25. Contact',
        intro: 'For questions regarding these Terms of Service, please contact:',
        emailLabel: 'Email:',
        email: 'care@geofleet.ai',
        websiteLabel: 'Website:',
        website: 'https://geofleet.ai',
      },
    },
  },
};
